import { apiClient } from "./apiClient";

/**
 * Obtiene la información de preview del partido según el ID
 * @param {string} matchId - ID del partido
 * @param {string} country - País para filtrar datos (parámetro query)
 * @returns {Promise<Object>} Datos de preview del partido
 */
export const getPreview = async (matchId, country = null) => {
  try {
    const response = await apiClient.get(
      `/api/core-clarosports/v2/events/match/${matchId}/preview`,
      {
        params: {
          ...(country === 'us' && { country })
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching preview data:", error.message);
    throw error;
  }
};

/**
 * Transforma los datos del API de preview a un formato compatible con los componentes
 * @param {Object} apiData - Datos crudos del API de preview
 * @returns {Object} Preview transformado
 */
export const transformPreviewData = (apiData) => {
  if (!apiData) return null;

  return {
    homeId: apiData.home_id,
    awayId: apiData.away_id,
    calendar: {
      home: apiData.calendar?.home || [],
      away: apiData.calendar?.away || [],
    },
    headToHead: {
      homeWins: apiData.head_to_head?.home_wins || 0,
      awayWins: apiData.head_to_head?.away_wins || 0,
      draws: apiData.head_to_head?.draws || 0,
      homeGoals: apiData.head_to_head?.home_goals || 0,
      awayGoals: apiData.head_to_head?.away_goals || 0,
      matches: apiData.head_to_head?.matches || [],
    },
    topScorers: {
      home: apiData.top_scorers?.home || [],
      away: apiData.top_scorers?.away || [],
    },
  };
};

/**
 * Obtiene el calendario de próximos partidos
 * @param {Object} previewData - Datos de preview transformados
 * @param {string} team - 'home' o 'away'
 * @returns {Array} Calendario del equipo
 */
export const getCalendar = (previewData, team) => {
  if (!previewData || !previewData.calendar) return [];
  const teamKey = team === 'home' ? 'home' : 'away';
  return previewData.calendar[teamKey] || [];
};

/**
 * Obtiene el historial de enfrentamientos
 * @param {Object} previewData - Datos de preview transformados
 * @returns {Object} Información de head-to-head
 */
export const getHeadToHeadStats = (previewData) => {
  if (!previewData || !previewData.headToHead) return null;
  return previewData.headToHead;
};

/**
 * Obtiene los partidos del historial
 * @param {Object} previewData - Datos de preview transformados
 * @returns {Array} Partidos previos
 */
export const getHeadToHeadMatches = (previewData) => {
  if (!previewData || !previewData.headToHead) return [];
  return previewData.headToHead.matches || [];
};

/**
 * Obtiene los goleadores del equipo
 * @param {Object} previewData - Datos de preview transformados
 * @param {string} team - 'home' o 'away'
 * @returns {Array} Lista de goleadores
 */
export const getTopScorers = (previewData, team) => {
  if (!previewData || !previewData.topScorers) return [];
  const teamKey = team === 'home' ? 'home' : 'away';
  return previewData.topScorers[teamKey] || [];
};
